/**
 * Componente para renderizar mensagens com botões interativos (button_list)
 * Exibe o texto da pergunta seguido por botões estilizados
 * Os botões são visuais apenas (não clicáveis no histórico)
 * 
 * VARIANTES:
 * - default: Estilo padrão com botões separados e bordas arredondadas
 * - whatsapp: Estilo WhatsApp com container único, divisórias, texto verde e ícone de reply
 */
import { cn } from "@/lib/utils";
import { Reply } from "lucide-react";

interface ButtonListMessageBubbleProps {
  content: string;
  options: Array<{ id: string; label: string }>;
  direction: 'inbound' | 'outbound';
  /** Variante visual dos botões */
  variant?: 'default' | 'whatsapp';
  /** Callback para clique na opção */
  onOptionClick?: (optionId: string, optionLabel: string) => void;
}

export function ButtonListMessageBubble({ 
  content, 
  options, 
  direction,
  variant = 'default',
  onOptionClick
}: ButtonListMessageBubbleProps) {
  // Estilo WhatsApp: texto e botões no MESMO container (bubble único)
  if (variant === 'whatsapp') {
    return (
      <div 
        className="flex flex-col overflow-hidden rounded-lg shadow-sm"
        style={{ backgroundColor: 'hsl(var(--chat-bubble-inbound))' }}
      >
        {/* Texto da pergunta - dentro do bubble */}
        {content && (
          <div 
            className="px-4 py-2.5 text-sm whitespace-pre-wrap break-words"
            style={{ color: 'hsl(var(--chat-text-primary))' }}
          >
            {content}
          </div>
        )}
        
        {/* Botões estilo WhatsApp - integrados ao bubble com ícone de reply */}
        {options && options.length > 0 && (
          <div style={{ borderTop: '1px solid hsl(var(--chat-border))' }}>
            {options.map((option, index) => (
              <button
                key={option.id}
                onClick={() => onOptionClick?.(option.id, option.label)}
                className="w-full px-4 py-3 text-sm transition-colors flex items-center justify-center gap-2 hover:opacity-80"
                style={{
                  color: '#00A884',
                  backgroundColor: 'hsl(var(--chat-bubble-inbound))',
                  borderTop: index !== 0 ? '1px solid hsl(var(--chat-border))' : 'none'
                }}
              >
                {/* Ícone de reply estilo WhatsApp */}
                <Reply className="h-4 w-4" />
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Estilo padrão: container único com texto + botões na mesma largura
  return (
    <div 
      className="rounded-lg overflow-hidden max-w-[280px]"
      style={{ backgroundColor: 'hsl(var(--chat-bubble-inbound))' }}
    >
      {/* Texto da pergunta - dentro do container unificado */}
      {content && (
        <div className="px-4 py-2.5">
          <p 
            className="text-sm whitespace-pre-wrap break-words"
            style={{ color: 'hsl(var(--chat-text-primary))' }}
          >
            {content}
          </p>
        </div>
      )}
      
      {/* Botões - mesma largura do texto, separados por divisória */}
      {options && options.length > 0 && (
        <div style={{ borderTop: content ? '1px solid hsl(var(--chat-border))' : 'none' }}>
          {options.map((option, index) => (
            <button
              key={option.id}
              onClick={() => onOptionClick?.(option.id, option.label)}
              className="w-full px-4 py-2.5 text-sm text-center transition-colors hover:opacity-80 break-words"
              style={{
                color: '#00a884',
                borderTop: index !== 0 ? '1px solid hsl(var(--chat-border))' : 'none'
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
